import React from "react";

import async from "./components/Async";

// All pages that rely on 3rd party components (other than Material-UI) are
// loaded asynchronously, to keep the initial JS bundle to a minimum size

// Layouts
import AuthLayout from "./layouts/Auth";
import DashboardLayout from "./layouts/Dashboard";

// Guards
import AuthGuard from "./components/guards/AuthGuard";

// Auth components
import SignIn from "./pages/auth/SignIn";
import SignUp from "./pages/auth/SignUp";
import ResetPassword from "./pages/auth/ResetPassword";
import Page404 from "./pages/auth/Page404";
import Page500 from "./pages/auth/Page500";
import SelectApp from "./pages/auth/SelectApp";

// Protected routes
import ProtectedPage from "./pages/protected/ProtectedPage";
import UserView from "./pages/users/UserView";
import MatchesGrid from "./pages/matches/MatchesGrid";
import MatchView from "./pages/matches/MatchView";
import DepositsGrid from "./pages/deposits/DepositsGrid";
import DepositView from "./pages/deposits/DepositView";
import WithdrawsGrid from "./pages/withdraws/WithdrawsGrid";
import WithdrawView from "./pages/withdraws/WithdrawView";
import RefundsGrid from "./pages/refunds/RefundsGrid";
import RefundView from "./pages/refunds/RefundView";
import { Navigate, NavLink, useParams } from "react-router-dom";
import SupportUsersPage from "./pages/supportUsers/UsersGrid";
import SupportUserView from "./pages/supportUsers/UserView";
import {
  GAME_USER_VIEW,
  GAME_MATCH_VIEW,
  SUPPORT_APP_USER_LIST,
  SUPPORT_APP_GAME_LIST,
  GAME_USER_DEPOSIT_VIEW,
  GAME_USER_WITHDRAWAL_VIEW,
  GAME_USER_REFUNDS_VIEW,
} from "./constants";
import SupportUserNew from "./pages/supportUsers/UserNew";
import GamesGrid from "./pages/games/GamesGrid";
import GamesNew from "./pages/games/GamesNew";
import GamesView from "./pages/games/GamesView";
import DepositRefund from "./pages/deposits/DepositRefund";
import { Helmet } from "react-helmet-async";
import {
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Link,
  Typography as MuiTypography,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import styled from "styled-components/macro";
import { display, spacing } from "@mui/system";
import useAuth from "./hooks/useAuth";

const Users = async(() => import("./pages/users/UsersGrid"));

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(display);

// TODO: Move that somewhere else - doesn't look like it's the right place
const SupportUserViewProfile = () => {
  const {
    user: { id: userId },
  } = useAuth();

  return (
    <>
      <Helmet title="Profile Settings" />

      <Typography variant="h3" gutterBottom display="inline">
        Profile Settings
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          <HomeIcon />
        </Link>
        <Typography>Profile Settings</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <SupportUserView userId={userId} />
    </>
  );
};

const SupportUserViewMain = () => {
  const { userId } = useParams();

  return (
    <>
      <Helmet title="Support User View" />

      <Typography variant="h3" gutterBottom display="inline">
        User {userId}
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          <HomeIcon />
        </Link>
        <Link component={NavLink} to="/support-users">
          Support Users
        </Link>
        <Typography>{userId}</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <SupportUserView userId={userId} />
    </>
  );
};

const routes = [
  {
    path: "/",
    element: <Navigate to={"/users"} />,
  },
  {
    path: "profile",
    element: (
      <AuthGuard requiredPermissions={[]}>
        <DashboardLayout>
          <SupportUserViewProfile />
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: "users",
    element: (
      <AuthGuard requiredPermissions={[GAME_USER_VIEW]}>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <Users />,
      },
      {
        path: ":userId",
        element: <UserView />,
      },
    ],
  },
  {
    path: "matches",
    element: (
      <AuthGuard requiredPermissions={[GAME_MATCH_VIEW]}>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <MatchesGrid />,
      },
      {
        path: ":matchId",
        element: <MatchView />,
      },
    ],
  },
  {
    path: "deposits",
    element: (
      <AuthGuard requiredPermissions={[GAME_USER_DEPOSIT_VIEW]}>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <DepositsGrid />,
      },
      {
        path: ":depositId",
        element: <DepositView />,
      },
      {
        path: ":depositId/refund",
        element: <DepositRefund />,
      },
    ],
  },
  {
    path: "withdraws",
    element: (
      <AuthGuard requiredPermissions={[GAME_USER_WITHDRAWAL_VIEW]}>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <WithdrawsGrid />,
      },
      {
        path: ":withdrawId",
        element: <WithdrawView />,
      },
    ],
  },
  {
    path: "refunds",
    element: (
      <AuthGuard requiredPermissions={[GAME_USER_REFUNDS_VIEW]}>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <RefundsGrid />,
      },
      {
        path: ":refundId",
        element: <RefundView />,
      },
    ],
  },
  {
    path: "support-users",
    element: (
      <AuthGuard requiredPermissions={[SUPPORT_APP_USER_LIST]}>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <SupportUsersPage />,
      },
      {
        path: "new",
        element: <SupportUserNew />,
      },
      {
        path: ":userId",
        element: <SupportUserViewMain />,
      },
    ],
  },
  {
    path: "games",
    element: (
      <AuthGuard requiredPermissions={[SUPPORT_APP_GAME_LIST]}>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <GamesGrid />,
      },
      {
        path: "new",
        element: <GamesNew />,
      },
      {
        path: ":gameId",
        element: <GamesView />,
      },
    ],
  },
  {
    path: "auth",
    element: <AuthLayout />,
    children: [
      {
        path: "sign-in",
        element: <SignIn />,
      },
      {
        path: "select",
        element: (
          <AuthGuard requiredPermissions={[]} disabledChecks={{ select: true }}>
            <SelectApp />
          </AuthGuard>
        ),
      },
      {
        path: "sign-up/:token",
        element: <SignUp />,
      },
      {
        path: "reset-password",
        element: <ResetPassword />,
      },
      {
        path: "reset-password/:refreshToken",
        element: <ResetPassword />,
      },
      {
        path: "404",
        element: <Page404 />,
      },
      {
        path: "500",
        element: <Page500 />,
      },
    ],
  },
  {
    path: "private",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <ProtectedPage />,
      },
    ],
  },
  {
    path: "*",
    element: <AuthLayout />,
    children: [
      {
        path: "*",
        element: <Page404 />,
      },
    ],
  },
];

export default routes;
